import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { faQrcode } from '@fortawesome/pro-solid-svg-icons';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { PatientModel } from 'models';
import { TagModel } from 'models/TagModel';

import { useEvent } from 'core/hooks';
import { useWindowSize } from 'core/hooks';
import { NotificationsService } from 'core/notifications';
import { ComponentSizes, DropdownField, MultiSelect, Window } from 'core/ui';

import { useApiClient } from 'features/api';
import { AuthenticationScheme, useAuthentication } from 'features/auth';
import { useSignalRRefresh } from 'features/auth/hooks/useSignalRRefresh';
import { Upload } from 'features/media/fragments/Upload';
import { ShareType } from 'features/patient/constants';
import { QRScanModal } from 'features/upload-exams/fragments/QRScanModal';

import { DocumentCategories } from '../../exam/constants';
import { UPLOAD_FILE_MODAL_CATEGORY_OPTIONS } from '../constants';
import { UploadFileCategory } from '../types';

// Helper to flatten tags
const flattenTagsForSelect = (tags: TagModel[]): TagModel[] => {
  return tags.reduce((acc: TagModel[], tag) => {
    acc.push({ ...tag, children: undefined });
    if (tag.children) {
      acc.push(...flattenTagsForSelect(tag.children));
    }
    return acc;
  }, []);
};

// Define BaseMeta/BaseBody if needed by UploadProps or callbacks
type BaseMeta = Record<string, unknown>;
type BaseBody = Record<string, unknown>;

// --- Define Metadata type for clarity ---
interface FileUploadMetadata {
  tags?: number[];
  filename?: string;
  captureTime?: Date | string | null;
  eventTime?: Date | string | null;
  // Add other per-file metadata if needed later
}
// --- End Metadata type ---

// Rename this interface and export it
export interface PatientFilesUploadModalProps {
  patient: PatientModel;
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // defaultCategory?: { name: string; value: any }; // REMOVED
  defaultTagIds?: number[];
  filterTagId?: number | null; // ADDED Prop for filter tag
  filterTagName?: string | null; // ADDED Prop for filter tag name
  onSuccess?: () => void;
  initialTag?: TagModel | null; // ADDED official prop
  initialTags?: TagModel[] | null; // ADDED: New prop for multiple initial tags
  worklistContext?: string | null; // ADDED: Context for upload display
}

// Add a stable empty array for default prop values
const EMPTY_DEFAULT_TAG_IDS: number[] = [];

enum UploadTab {
  LOCAL = 'local',
  MOBILE = 'mobile',
}

// Define a more specific type for category options if possible
type CategoryOption = { name: string; value: UploadFileCategory | number };

interface FormValues {
  category: CategoryOption;
  tags: number[];
}

// --- Category Value Lookups ---
// REMOVE categoryShorthandToIdMap and getNumericCategoryId helper
/*
const categoryShorthandToIdMap = new Map<string, number>();
Object.values(DocumentCategories).forEach((cat) => {
  if (cat && typeof cat === 'object' && cat.value && cat.shorthand) {
    categoryShorthandToIdMap.set(cat.shorthand, cat.value);
  }
});

// Helper to consistently get numeric category ID
const getNumericCategoryId = (categoryValue: number | UploadFileCategory): number | null => {
  if (typeof categoryValue === 'number') {
    return categoryValue;
  }
  // If it's a string (shorthand), look up the ID
  return categoryShorthandToIdMap.get(categoryValue) ?? null;
};
*/
// --- End Lookups ---

export const PatientMediaUploadModal: FC<PatientFilesUploadModalProps> = ({
  patient,
  onClose,
  // defaultCategory, // REMOVED
  defaultTagIds = EMPTY_DEFAULT_TAG_IDS, // USE STABLE EMPTY ARRAY
  filterTagId, // ADDED
  filterTagName, // ADDED
  onSuccess,
  initialTag, // ADDED: Destructure the new prop
  initialTags, // ADDED: Destructure new prop
  worklistContext, // ADDED: Destructure context prop
}) => {
  const apiClient = useApiClient();
  // REMOVE selectedCategory state
  // const [selectedCategory, setSelectedCategory] = useState<CategoryOption>(defaultCategory ?? UPLOAD_FILE_MODAL_CATEGORY_OPTIONS[0]);
  const [linkId, setLinkId] = useState<string>('');
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const { activeScheme } = useAuthentication();
  const windowSize = useWindowSize();
  const [allTagsFlat, setAllTagsFlat] = useState<TagModel[]>([]);
  const [allHierarchicalTags, setAllHierarchicalTags] = useState<TagModel[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [tagIdToTagMap, setTagIdToTagMap] = useState<Map<number, TagModel>>(new Map());
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  // Get SignalR connection for sending notifications
  const { sendEvent } = useSignalRRefresh({});

  // Fetch all tags (simplified - no lookup map creation needed here anymore)
  useEffect(() => {
    const fetchTags = async () => {
      setIsLoadingTags(true);
      try {
        const tags = await apiClient.tagsClient.getLevelTags();
        setAllHierarchicalTags(tags);
        const flatTags = flattenTagsForSelect(tags);
        setAllTagsFlat(flatTags);
        // Build ID map needed for MultiSelect value conversion
        const idMap = new Map<number, TagModel>();
        flatTags.forEach((tag) => {
          idMap.set(tag.id, tag);
        });
        setTagIdToTagMap(idMap);
      } catch (error) {
        console.error('Failed to fetch tags:', error);
        NotificationsService.displayError('Could not load tags.');
        setAllTagsFlat([]);
        setAllHierarchicalTags([]);
        setTagIdToTagMap(new Map());
      } finally {
        setIsLoadingTags(false);
      }
    };
    fetchTags();
  }, [apiClient]);

  // Initialize selected tags based on the new initialTags prop or fallbacks
  const resolvedInitialTagIds = useMemo(() => {
    if (initialTags && initialTags.length > 0) {
      return initialTags.map((tag) => tag.id);
    }
    if (initialTag) {
      return [initialTag.id];
    }
    return defaultTagIds;
  }, [initialTags, initialTag, defaultTagIds]);

  // Add form methods for react-hook-form
  const formMethods = useForm<FormValues>({
    defaultValues: {
      // REMOVE category
      // category: selectedCategory,
      tags: resolvedInitialTagIds,
    },
  });

  // Update form default value - remove time fields
  useEffect(() => {
    formMethods.reset({
      // REMOVE category
      // category: selectedCategory,
      tags: resolvedInitialTagIds,
    });
    // REMOVE selectedCategory from dependency array
  }, [resolvedInitialTagIds, formMethods.reset]); // USE formMethods.reset (stable)

  // Handle category changes - REMOVE handler
  /*
  const handleCategoryChange = useEvent((event: DropDownListChangeEvent) => {
    const newCategory = event.target.value as CategoryOption;
    if (newCategory) {
      setSelectedCategory(newCategory);
      formMethods.setValue('category', newCategory);
      // --- NO TAG UPDATES ON CATEGORY CHANGE --- //
    }
  });
  */

  // This is now the primary upload handler, called from Upload.tsx for each file.
  const handleFileUploaded = useCallback(
    async (file: File | Blob, metadata?: FileUploadMetadata): Promise<void> => {
      try {
        const finalFilename = metadata?.filename ?? (file instanceof File ? file.name : `upload-${Date.now()}`);
        // Ensure we have a File object with the correct name, as the `add` method uses the File.name property.
        const fileToSend = new File([file], finalFilename, { type: file.type });

        // Get tag IDs and times from metadata for subsequent updates
        const selectedTagIds = metadata?.tags ?? [];
        const captureTimeValue = metadata?.captureTime;
        const eventTimeValue = metadata?.eventTime;

        // Step 1: Upload the file
        const uploadedFilesResults = await apiClient.filesClient.add(
          [fileToSend],
          false, // shouldSplitFiles
          null, // examId
          patient.id,
          null, // categoryId
          activeScheme === AuthenticationScheme.OIDC ? 'msal-required' : 'share-required',
        );

        if (!uploadedFilesResults?.[0]?.id) {
          throw new Error('Upload API call succeeded but returned no file data.');
        }

        const uploadedFile = uploadedFilesResults[0];
        NotificationsService.displaySuccess(`${finalFilename} uploaded successfully`);

        // Step 2: Update tags if they were provided
        const realTagIds = selectedTagIds.filter((id) => id >= 0);
        if (realTagIds.length > 0) {
          try {
            await apiClient.filesClient.updateFileTags(
              uploadedFile.id,
              realTagIds,
              activeScheme === AuthenticationScheme.OIDC ? 'msal-required' : 'share-required',
            );
          } catch (tagError: unknown) {
            console.error(`Failed to update tags for file ${uploadedFile.id}:`, tagError);
            NotificationsService.displayError(`Failed to set tags for ${uploadedFile.fileName}. Please edit tags manually.`);
          }
        }

        // Step 3: Update timestamps if they were provided
        const patchDoc: { op: string; path: string; value: string | null }[] = [];
        if (captureTimeValue) {
          const formattedCaptureTime =
            captureTimeValue instanceof Date ? dayjs(captureTimeValue).toISOString() : typeof captureTimeValue === 'string' ? captureTimeValue : null;
          if (formattedCaptureTime) {
            patchDoc.push({ op: 'replace', path: '/captureTime', value: formattedCaptureTime });
          }
        }
        if (eventTimeValue) {
          const formattedEventTime =
            eventTimeValue instanceof Date ? dayjs(eventTimeValue).toISOString() : typeof eventTimeValue === 'string' ? eventTimeValue : null;
          if (formattedEventTime) {
            patchDoc.push({ op: 'replace', path: '/eventTime', value: formattedEventTime });
          }
        }

        if (patchDoc.length > 0) {
          try {
            await apiClient.filesClient.renameFile(uploadedFile.id, patchDoc);
          } catch (tsError: unknown) {
            console.error(`Failed to update timestamps for file ${uploadedFile.id}:`, tsError);
            NotificationsService.displayError(`Failed to set timestamps for ${uploadedFile.fileName}. Please edit them manually.`);
          }
        }

        // Step 4: Call success callback if provided
        if (onSuccess) {
          onSuccess();
        }

        // Step 5: Send SignalR notification for real-time refresh
        try {
          await sendEvent('patientFileUploaded', { patientId: patient.id });
        } catch (wsError) {
          console.error('Failed to send SignalR notification:', wsError);
          // Don't show error to user as this is not critical
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        NotificationsService.displayError('Failed to upload file');
        // Re-throw the error so Promise.allSettled in parent can see it failed
        throw error;
      }
    },
    [activeScheme, apiClient.filesClient, onSuccess, patient.id, sendEvent],
  );

  // --- Function to generate link ID and show QR modal ---
  const handleGenerateAndShowQrCode = useCallback(async () => {
    setIsGeneratingLink(true);
    const generatedLinkId = crypto.randomUUID(); // Generate UUID locally
    setLinkId(generatedLinkId); // Set state immediately for the modal

    try {
      // Uncomment and use the API call to register the link
      const expireOn = dayjs().add(1, 'day').format('YYYY-MM-DDTHH:mm:ss');

      // REMOVE category ID lookup and pass null or context-specific message
      await apiClient.studyShare.addStudyShare(
        {
          id: 0, // New record
          linkId: generatedLinkId,
          email: null,
          sharePassword: null,
          // Use ShareType.Link, or a specific PatientUpload type if available
          shareType: ShareType.Link,
          sharePatientId: patient.id, // Associate with the patient
          studyShareExams: [], // No specific exams for this type of share
          expireOn: expireOn,
          dateCreated: '', // Handled by backend
          message: initialTag ? `TAG_ID:${initialTag.id}` : (filterTagName ?? 'Patient File Upload'), // Revert to TAG_ID only
          bypassSplashPage: true, // Assuming this is desired
        },
        activeScheme === AuthenticationScheme.OIDC ? 'msal-required' : 'share-required',
      );
      // --- End API call ---

      // If API call is successful:
      setQrModalVisible(true); // Show modal only after successful link registration
    } catch (error) {
      console.error('Failed to generate/register QR code link:', error);
      NotificationsService.displayError('Could not generate QR code link. Please try again.');
      setLinkId(''); // Clear linkId on error
    } finally {
      setIsGeneratingLink(false);
    }
    // Add filterTagName dependency
  }, [patient.id, apiClient, filterTagName, activeScheme, initialTag]);

  // Close the QR modal
  const handleQrModalClose = () => {
    setQrModalVisible(false);
  };

  // Determine modal title dynamically
  const modalTitle = filterTagName ? `Upload ${filterTagName}` : 'Upload Media';

  return (
    <>
      {/* Pass dynamic title */}
      <Window title={modalTitle} modal width={windowSize.width > 900 ? 900 : windowSize.width} onClose={onClose}>
        <FormProvider {...formMethods}>
          <StyledContainer>
            <StyledHeader>
              {/* REMOVED Category Dropdown */}
              {/*
              <DropdownField
                name="category"
                label="Category"
                data={UPLOAD_FILE_MODAL_CATEGORY_OPTIONS}
                dataItemKey="value"
                textField="name"
                value={selectedCategory}
                onChange={handleCategoryChange}
              />
              */}
            </StyledHeader>

            <StyledContent>
              <Upload
                // category={String(selectedCategory.value)} // REMOVED category prop
                onFileUploaded={handleFileUploaded}
                patientId={patient.id}
                onGenerateQrCode={handleGenerateAndShowQrCode}
                isGeneratingQrCode={isGeneratingLink}
                availableTags={allHierarchicalTags}
                isLoadingTags={isLoadingTags}
                initialTagIds={resolvedInitialTagIds}
                onUploadComplete={onClose}
                filterTagName={filterTagName}
                worklistContext={worklistContext}
                showDescriptionField={true}
              />
            </StyledContent>
          </StyledContainer>
        </FormProvider>
      </Window>

      {/* REMOVED Overlays (ScreenCapture, Webcam) - Moved to Upload.tsx */}

      {/* ADD QR Modal BACK */}
      {qrModalVisible && linkId && <QRScanModal linkId={linkId} onClose={handleQrModalClose} show={qrModalVisible} examId={null} />}
    </>
  );
};

// ---------------------- Styled Components ----------------------
const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  padding: 15px;
  border-bottom: 1px solid #e0e0e0;
`;

const StyledContent = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const StyledMobileSection = styled.div`
  padding: 16px;
`;
